import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";


export default function AvatarOverlay({ user, userDetails, stats }) {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const shellRef = useRef(null);
  const moRef = useRef(null);
  const portalRoot = useMemo(() => document.getElementById("overlays") || document.body, []);
  const avatarUrl = user?.photo_url || "https://i.imgur.com/default-avatar.png";

  const deposited = stats?.deposited ?? userDetails?.total_deposit ?? "-";
  const withdrawn = stats?.withdrawn ?? userDetails?.total_withdrawn ?? "-";
  const partners =
    stats?.partners ??
    ((userDetails?.referrals?.level1 || 0) +
      (userDetails?.referrals?.level2 || 0) +
      (userDetails?.referrals?.level3 || 0));
  const partnerEarnings =
    stats?.partner_earnings ?? userDetails?.partner_earnings ?? "-";

  useEffect(() => setMounted(true), []);

  const setVar = (n, v) => document.documentElement.style.setProperty(n, v);

  const placePanel = () => {
    if (!shellRef.current) return;
    const r = shellRef.current.getBoundingClientRect();
    const gap = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--av-panel-gap") || "6");
    const top = Math.round(r.bottom + gap);
    setVar("--av-panel-top", top + "px");
  };

  useEffect(() => {
    placePanel();
    const vv = window.visualViewport;
    const onR = () => placePanel();
    vv?.addEventListener("resize", onR);
    vv?.addEventListener("scroll", onR);
    window.addEventListener("resize", onR);
    window.addEventListener("orientationchange", onR);
    return () => {
      vv?.removeEventListener("resize", onR);
      vv?.removeEventListener("scroll", onR);
      window.removeEventListener("resize", onR);
      window.removeEventListener("orientationchange", onR);
    };
  }, []);

  useEffect(() => {
    const selectors = [
      ".ReactModal__Overlay",
      "[data-radix-dialog-overlay]",
      "[data-reach-dialog-overlay]",
      '[role="dialog"].overlay',
      '[class*="ModalOverlay"]',
      '[class*="DialogOverlay"]',
      '[class*="modal-overlay"]',
      "dialog[open]"
    ].join(",");
    const check = () => {
      const nodes = Array.from(document.querySelectorAll(selectors));
      const any = nodes.some(el => !el.closest(".av-drop"));
      if (any) setOpen(false);
    };
    check();
    if (!moRef.current) moRef.current = new MutationObserver(check);
    moRef.current.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ["style", "class", "open", "aria-hidden", "aria-modal"] });
    return () => moRef.current && moRef.current.disconnect();
  }, []);

  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!mounted || dismissed) return null;

  const panel = (
    <>
      <div className={`av-scrim ${open ? "av-open" : ""}`} onClick={() => setOpen(false)} />
      <div className={`av-drop ${open ? "av-open" : ""}`}>
        <div className="av-dropHead">
          <div className="av-dropTitle">Профиль</div>
          <button className="av-dropClose" onClick={() => setOpen(false)} aria-label="close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M8 14l4-4 4 4" stroke="#cbbfff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        <div className="av-dropBody">
          <div className="av-row"><div className="av-label">ID</div><div className="av-value">{user?.telegram_id || "-"}</div></div>
          <div className="av-row"><div className="av-label">Username</div><div className="av-value">{user?.username ? `@${user.username}` : "-"}</div></div>
          <div className="av-row"><div className="av-label">Имя</div><div className="av-value">{user?.first_name || "-"}</div></div>
          <div className="av-row"><div className="av-label">Язык</div><div className="av-value">{user?.language_code || "RU"}</div></div>
          <div className="av-sep"></div>
          <div className="av-row"><div className="av-label">Пополнено</div><div className="av-value">{deposited}</div></div>
          <div className="av-row"><div className="av-label">Выведено</div><div className="av-value">{withdrawn}</div></div>
          <div className="av-row"><div className="av-label">Партнёров</div><div className="av-value">{partners}</div></div>
          <div className="av-row"><div className="av-label">Заработано с партнёров</div><div className="av-value">{partnerEarnings}</div></div>
          <div className="av-actions">
            <button className="av-btnGhost" onClick={() => setOpen(false)}>Свернуть</button>
            <button className="av-btnPrimary" onClick={() => setConfirmOpen(true)}>Скрыть</button>
          </div>
        </div>
      </div>
    </>
  );

  const node = (
    <>
      <div className="av-shell" ref={shellRef}>
        <div className="av-card">
          <svg className="av-svg" width="50" height="50" viewBox="0 0 62 62" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs><clipPath id="avClip"><circle cx="31" cy="31" r="17" /></clipPath></defs>
            <circle cx="31" cy="31" r="21" fill="none" stroke="#7c3aed" strokeWidth="2" />
            <image href={avatarUrl} x="14" y="14" width="34" height="34" clipPath="url(#avClip)" />
          </svg>
          <button className="av-toggle" onClick={() => { placePanel(); setOpen(true); }} aria-label="open">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M8 10l4 4 4-4" stroke="#cbbfff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      {createPortal(panel, portalRoot)}

      {confirmOpen && createPortal(
        <div className="av-confirmOverlay" role="dialog" aria-modal="true">
          <div className="av-confirmCard">
            <div className="av-confirmHead">
              <div className="av-confirmTitle">Скрыть окно с аватаром?</div>
              <div className="av-confirmSub">Окно исчезнет сейчас и появится снова при следующем открытии приложения.</div>
            </div>
            <div className="av-confirmActions">
              <button className="av-btnGhost" onClick={() => setConfirmOpen(false)}>Нет</button>
              <button className="av-btnPrimary" onClick={() => { setConfirmOpen(false); setOpen(false); setDismissed(true); }}>Да, скрыть</button>
            </div>
          </div>
        </div>,
        portalRoot
      )}
    </>
  );

  return node;
}
